'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ProtectedRoute } from '@/lib/auth';
import Link from 'next/link';
import RecommendationsSection from '@/components/RecommendationsSection';
import LearningPathsSection from '@/components/LearningPathsSection';
import { trackSignup, getUTMParams, setUserProperties, type SignupTier } from '@/lib/analytics';
import DashboardWidget from '@/components/dashboard/DashboardWidget';
import StreakIndicator from '@/components/dashboard/StreakIndicator';
import UsageInsights from '@/components/dashboard/UsageInsights';
import BadgeGrid from '@/components/dashboard/BadgeGrid';
import ChallengeCards from '@/components/dashboard/ChallengeCards';
import AchievementCelebration from '@/components/dashboard/AchievementCelebration';
import DashboardNotifications from '@/components/dashboard/DashboardNotifications';
import RecentlyViewed from '@/components/RecentlyViewed';
import { Trophy, Flame, TrendingUp, Clock, Shield, Settings, Loader2 } from 'lucide-react';
import { Badge } from '@/components/dashboard/BadgeDisplay';

/**
 * Get display name from user data
 * Prefers name if it doesn't look like an email, otherwise extracts from email
 */
function getDisplayName(name: string | undefined, email: string | undefined): string {
  // If name exists and doesn't look like an email, use it
  if (name && !name.includes('@')) {
    // Get first name only for friendlier greeting
    return name.split(' ')[0];
  }

  // Fall back to extracting name from email
  if (email) {
    const localPart = email.split('@')[0];
    // Capitalize first letter and handle common patterns
    return localPart.charAt(0).toUpperCase() + localPart.slice(1).replace(/[._]/g, ' ').split(' ')[0];
  }

  return 'there';
}

interface DashboardData {
  user: {
    id: string;
    email: string;
    name: string;
    picture?: string;
    isAdmin?: boolean;
  };
  stats: {
    articlesRead: number;
    toolsUsed: number;
    activityThisWeek: number;
    savedItems: number;
  };
  insights?: {
    articlesThisMonth: number;
    articlesLastMonth: number;
    toolsThisMonth: number;
    toolsLastMonth: number;
    mostUsedTool: string | null;
  };
  streaks?: {
    current: number;
    longest: number;
    lastActivity: string | null;
    active: boolean;
  };
  badges?: {
    earnedCount: number;
    totalCount: number;
    earned: Badge[];
    recentlyEarned: Badge | null;
  };
  recentActivity: any[];
  savedContent: any[];
  continueReading: any[];
  subscription: {
    plan: string;
    status: string;
    current_period_end?: string;
  };
}

function DashboardContent() {
  const router = useRouter();
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [celebrationBadge, setCelebrationBadge] = useState<Badge | null>(null);

  useEffect(() => {
    async function fetchDashboard() {
      try {
        const response = await fetch('/api/secure/dashboard', {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to load dashboard');
        }

        const result = await response.json();
        setData(result.data);

        // Show celebration for newly earned badge
        if (result.data?.badges?.recentlyEarned) {
          setCelebrationBadge(result.data.badges.recentlyEarned);
        }

        // Redirect new users to onboarding if not completed
        // Check sessionStorage first (set by onboarding page on completion/skip)
        const onboardingDone = typeof window !== 'undefined' &&
          sessionStorage.getItem('onboarding_completed') === 'true';

        if (!onboardingDone) {
          try {
            const prefsRes = await fetch('/api/secure/preferences', { credentials: 'include' });
            if (prefsRes.ok) {
              const prefsData = await prefsRes.json();
              if (prefsData.data && prefsData.data.onboarding_completed === false) {
                router.push('/dashboard/onboarding');
                return;
              }
            }
          } catch {
            // Non-blocking - continue to dashboard if prefs check fails
          }
        }

        // Track new user signup if this is their first visit
        if (typeof window !== 'undefined') {
          const isNewUser = localStorage.getItem('is_new_user');
          if (isNewUser === 'true' && result.data?.user) {
            const tier = (result.data.subscription?.plan || 'free').toLowerCase() as SignupTier;
            const source = localStorage.getItem('signup_source') || 'dashboard';
            const utmParams = getUTMParams();

            // Track the signup
            trackSignup(tier, {
              method: 'email', // Default, could be enhanced with actual method
              source,
              campaign: utmParams.campaign || localStorage.getItem('utm_campaign') || undefined,
            });

            // Set user properties for segmentation
            setUserProperties({
              user_id: result.data.user.id,
              tier,
              is_premium: tier !== 'free',
            });

            // Clear the flags
            localStorage.removeItem('is_new_user');
            localStorage.removeItem('signup_source');
            localStorage.removeItem('utm_campaign');
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        setIsLoading(false);
      }
    }

    fetchDashboard();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-accent animate-spin" />
          <div className="text-foreground text-lg">Loading your dashboard...</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-red-400">{error || 'Failed to load dashboard'}</div>
      </div>
    );
  }

  const getContentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      insight: 'Insight',
      framework: 'Framework',
      comparison: 'Comparison',
      code: 'Code Sample',
      architecture: 'Architecture',
      template: 'Template',
      'case-study': 'Case Study'
    };
    return labels[type] || type;
  };

  const getContentUrl = (type: string, slug: string) => {
    return `/${type === 'case-study' ? 'case-studies' : type}/${slug}`;
  };

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Achievement Celebration Modal */}
        <AchievementCelebration
          badge={celebrationBadge}
          onClose={() => setCelebrationBadge(null)}
        />

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2 text-foreground">
                Welcome back, {getDisplayName(data.user.name, data.user.email)}! 👋
              </h1>
              <p className="text-foreground-secondary">
                Here&apos;s what&apos;s happening with your CTO learning journey
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* Streak Indicator */}
              {data.streaks && (
                <StreakIndicator
                  currentStreak={data.streaks.current}
                  longestStreak={data.streaks.longest}
                  streakActive={data.streaks.active}
                  variant="compact"
                />
              )}

              <Link
                href="/dashboard/settings"
                className="px-4 py-2 bg-surface border border-border text-accent rounded-lg hover:bg-accent-muted transition-colors flex items-center gap-2"
              >
                <Settings className="w-5 h-5" />
                Settings
              </Link>
              <Link
                href="/dashboard/achievements"
                className="px-4 py-2 bg-surface border border-border text-accent rounded-lg hover:bg-accent-muted transition-colors flex items-center gap-2"
              >
                <Trophy className="w-5 h-5" />
                Achievements
              </Link>
              {data.user.isAdmin && (
                <Link
                  href="/admin"
                  className="px-4 py-2 bg-surface border border-border text-accent rounded-lg hover:bg-accent-muted transition-colors flex items-center gap-2"
                >
                  <Shield className="w-5 h-5" />
                  Admin
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-surface border border-border rounded-lg p-6">
            <div className="text-3xl font-bold text-accent mb-1">
              {data.stats.articlesRead}
            </div>
            <div className="text-sm text-foreground-secondary">Articles Read</div>
          </div>

          <div className="bg-surface border border-border rounded-lg p-6">
            <div className="text-3xl font-bold text-accent mb-1">
              {data.stats.savedItems}
            </div>
            <div className="text-sm text-foreground-secondary">Saved Items</div>
          </div>

          <div className="bg-surface border border-border rounded-lg p-6">
            <div className="text-3xl font-bold text-accent mb-1">
              {data.stats.toolsUsed}
            </div>
            <div className="text-sm text-foreground-secondary">Tools Used</div>
          </div>

          <div className="bg-surface border border-border rounded-lg p-6">
            <div className="text-3xl font-bold text-accent mb-1">
              {data.stats.activityThisWeek}
            </div>
            <div className="text-sm text-foreground-secondary">This Week</div>
          </div>
        </div>

        {/* Subscription Status */}
        {data.subscription.plan === 'free' && (
          <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
            <div className="flex flex-col md:flex-row items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-accent mb-2">
                  Unlock Premium Content
                </h3>
                <p className="text-foreground-secondary mb-4">
                  Access all frameworks, advanced tools, templates, and exclusive resources. Upgrade to Pro for full access.
                </p>
                <ul className="space-y-2 text-sm text-foreground-secondary">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>All premium frameworks & playbooks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Interactive decision tools & calculators</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Ready-to-use templates & documents</span>
                  </li>
                </ul>
              </div>
              <div className="flex flex-col gap-2">
                <Link
                  href="/pricing"
                  className="px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors text-center whitespace-nowrap"
                >
                  View Pricing
                </Link>
                <Link
                  href="/dashboard/billing"
                  className="px-6 py-3 border-2 border-accent text-accent font-semibold rounded-lg hover:bg-accent-muted transition-colors text-center whitespace-nowrap"
                >
                  Upgrade Now
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Continue Reading */}
            {data.continueReading.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-foreground">
                    Continue Where You Left Off
                  </h2>
                </div>

                <div className="space-y-4">
                  {data.continueReading.map((item) => (
                    <Link
                      key={`${item.content_type}-${item.content_slug}`}
                      href={getContentUrl(item.content_type, item.content_slug)}
                      className="block bg-surface border border-border rounded-lg p-6 hover:border-border-muted0 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs uppercase tracking-wider text-accent">
                          {getContentTypeLabel(item.content_type)}
                        </span>
                        <span className="text-sm text-foreground-secondary">
                          {item.progress_percent}% complete
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {item.content_slug.split('-').map((word: string) =>
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                      </h3>
                      <div className="w-full bg-background/50 rounded-full h-2">
                        <div
                          className="bg-accent rounded-full h-2 transition-all"
                          style={{ width: `${item.progress_percent}%` }}
                        />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Saved Content */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-foreground">
                  Saved Content ({data.stats.savedItems})
                </h2>
                {data.stats.savedItems > 6 && (
                  <Link
                    href="/dashboard/saved"
                    className="text-accent hover:text-foreground transition-colors text-sm"
                  >
                    View all →
                  </Link>
                )}
              </div>

              {data.savedContent.length === 0 ? (
                <div className="bg-surface border border-border rounded-lg p-8 text-center">
                  <svg
                    className="w-16 h-16 mx-auto mb-4 text-accent/30"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                    />
                  </svg>
                  <p className="text-foreground-secondary mb-4">
                    You haven&apos;t saved any content yet
                  </p>
                  <p className="text-sm text-foreground-tertiary">
                    Click the bookmark icon on any article or resource to save it here
                  </p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {data.savedContent.map((item) => (
                    <Link
                      key={item.id}
                      href={getContentUrl(item.content_type, item.content_slug)}
                      className="bg-surface border border-border rounded-lg p-4 hover:border-border-muted0 transition-colors"
                    >
                      <span className="text-xs uppercase tracking-wider text-accent block mb-2">
                        {getContentTypeLabel(item.content_type)}
                      </span>
                      <h3 className="text-sm font-semibold text-foreground mb-2">
                        {item.content_slug.split('-').map((word: string) =>
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                      </h3>
                      {item.notes && (
                        <p className="text-xs text-foreground-tertiary line-clamp-2">
                          {item.notes}
                        </p>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Notifications */}
            <DashboardNotifications
              streakData={data.streaks ? {
                currentStreak: data.streaks.current,
                streakActive: data.streaks.active,
                lastActivityDate: data.streaks.lastActivity,
              } : undefined}
              badgeData={data.badges ? {
                earnedCount: data.badges.earnedCount,
                totalCount: data.badges.totalCount,
                recentlyEarned: data.badges.recentlyEarned,
              } : undefined}
            />

            {/* Usage Insights */}
            {data.insights && data.streaks && data.badges && (
              <DashboardWidget
                title="Your Progress"
                icon={<TrendingUp className="w-5 h-5" />}
              >
                <UsageInsights
                  articlesThisMonth={data.insights.articlesThisMonth}
                  articlesLastMonth={data.insights.articlesLastMonth}
                  toolsThisMonth={data.insights.toolsThisMonth}
                  toolsLastMonth={data.insights.toolsLastMonth}
                  mostUsedTool={data.insights.mostUsedTool}
                  currentStreak={data.streaks.current}
                  badgesEarned={data.badges.earnedCount}
                  totalBadges={data.badges.totalCount}
                />
              </DashboardWidget>
            )}

            {/* Badge Showcase */}
            {data.badges && data.badges.earned.length > 0 && (
              <DashboardWidget
                title="Recent Badges"
                icon={<Trophy className="w-5 h-5" />}
                action={
                  <Link
                    href="/dashboard/achievements"
                    className="text-sm text-accent hover:text-accent"
                  >
                    View all →
                  </Link>
                }
              >
                <BadgeGrid
                  badges={data.badges.earned}
                  maxDisplay={6}
                  size="sm"
                />
                <p className="text-xs text-foreground-tertiary mt-3">
                  {data.badges.earnedCount} of {data.badges.totalCount} badges earned
                </p>
              </DashboardWidget>
            )}

            {/* Challenges */}
            <DashboardWidget
              title="Challenges"
              icon={<Flame className="w-5 h-5" />}
            >
              <ChallengeCards />
            </DashboardWidget>

            {/* Recently Viewed */}
            <DashboardWidget
              title="Recently Viewed"
              icon={<Clock className="w-5 h-5" />}
            >
              <RecentlyViewed
                limit={5}
                showHeader={false}
                variant="compact"
              />
            </DashboardWidget>

            {/* Learning Paths */}
            <LearningPathsSection />

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-bold text-foreground mb-4">Quick Links</h3>
              <div className="space-y-2">
                <Link
                  href="/command-center"
                  className="block bg-accent-muted border border-border rounded-lg p-3 hover:border-border-muted0 transition-colors text-sm text-accent font-semibold flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                  Command Center →
                </Link>
                <Link
                  href="/paths"
                  className="block bg-surface border border-border rounded-lg p-3 hover:border-border-muted0 transition-colors text-sm text-foreground"
                >
                  📚 Learning Paths →
                </Link>
                <Link
                  href="/refer"
                  className="block bg-surface border border-border rounded-lg p-3 hover:border-border-muted0 transition-colors text-sm text-foreground"
                >
                  🎁 Refer Friends →
                </Link>
                <Link
                  href="/insights"
                  className="block bg-surface border border-border rounded-lg p-3 hover:border-border-muted0 transition-colors text-sm text-foreground"
                >
                  Browse Insights →
                </Link>
                <Link
                  href="/frameworks"
                  className="block bg-surface border border-border rounded-lg p-3 hover:border-border-muted0 transition-colors text-sm text-foreground"
                >
                  View Frameworks →
                </Link>
                <Link
                  href="/tools"
                  className="block bg-surface border border-border rounded-lg p-3 hover:border-border-muted0 transition-colors text-sm text-foreground"
                >
                  Decision Tools →
                </Link>
                <Link
                  href="/splitcause"
                  className="block bg-surface border border-border rounded-lg p-3 hover:border-border-muted0 transition-colors text-sm text-foreground"
                >
                  Split Cause (RCA) →
                </Link>
                <Link
                  href="/dashboard/referrals"
                  className="block bg-surface border border-border rounded-lg p-3 hover:border-border-muted0 transition-colors text-sm text-foreground"
                >
                  Referral Stats →
                </Link>
              </div>
            </div>

            {/* Recommendations */}
            <RecommendationsSection />

            {/* Recent Activity */}
            {data.recentActivity.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-foreground mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {data.recentActivity.slice(0, 5).map((activity, index) => {
                    // Convert slug to readable title
                    const contentTitle = activity.content_slug
                      ? activity.content_slug.split('-').map((word: string) =>
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')
                      : getContentTypeLabel(activity.content_type);

                    return (
                      <Link
                        key={index}
                        href={getContentUrl(activity.content_type, activity.content_slug)}
                        className="block text-sm hover:bg-surface rounded-lg p-2 -m-2 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-accent rounded-full mt-1.5 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-foreground-secondary">
                              {activity.activity_type === 'read' && 'Read'}
                              {activity.activity_type === 'saved' && 'Saved'}
                              {activity.activity_type === 'used_tool' && 'Used'}
                              {activity.activity_type === 'downloaded' && 'Downloaded'}
                              {' '}
                              <span className="text-accent">
                                {contentTitle}
                              </span>
                            </p>
                            <p className="text-xs text-foreground-tertiary">
                              {new Date(activity.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  return (
    <ProtectedRoute>
      <DashboardContent />
    </ProtectedRoute>
  );
}
